import {
  faGlobeEurope,
  faGolfBallTee,
  faMagnifyingGlass,
  faMapPin,
  IconDefinition,
} from "@fortawesome/free-solid-svg-icons";
import clsx from "clsx";
import React, {
  useCallback,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";

import ListItem from "@/components/atoms/ListItem";
import List from "@/components/compounds/List";
import Loading from "@/components/molecules/Loading";
import { ColorEnum } from "@/enums";
import { SitewideResultTypeEnum } from "@/search/api/enums";
import { HomepageSearchResult } from "@/search/api/types";
import { debounce } from "@/utils/helpers";

import { useHomepageSearchQuery } from "../api/useHomepageSearchQuery";
import { useSuggestionListQuery } from "../api/useSuggestionListQuery";
import Popup from "./Popup";
import styles from "./SearchAutocomplete.module.css";

const ref = "search-autocomplete";

const ICON_MAP: Partial<Record<SitewideResultTypeEnum, IconDefinition>> = {
  [SitewideResultTypeEnum.SEARCH]: faMagnifyingGlass,
  [SitewideResultTypeEnum.REGION]: faGlobeEurope,
  [SitewideResultTypeEnum.VENUE]: faGolfBallTee,
  [SitewideResultTypeEnum.PLACE]: faMapPin,
};

const SEARCH_DEBOUNCE = 500;

export interface SearchAutocompleteHandle {
  focusInput: () => void;
}

interface SearchAutocompleteProps {
  id?: string;
  label?: string;
  labelInside?: boolean;
  placeholder?: string;
  noResultsText?: string;
  className?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onSelectItem?: (item: HomepageSearchResult) => void;
}

function SearchAutocomplete(
  props: SearchAutocompleteProps,
  forwardedRef: React.ForwardedRef<SearchAutocompleteHandle>,
) {
  const {
    label,
    placeholder = "Search...",
    noResultsText = "No results",
    className,
    onChange,
    onSelectItem,
  } = props;

  const inputRef = useRef<HTMLInputElement>(null);
  const wrapperRef = useRef<HTMLElement>(null);

  useImperativeHandle(forwardedRef, () => ({
    focusInput: handleFocusInput,
  }));

  const [selected, setSelected] = useState<HomepageSearchResult | null>(null);
  const [inputValue, setInputValue] = useState("");
  const [popupVisible, setPopupVisible] = useState(false);
  const [isShowSuggestion, setIsShowSuggestion] = useState(true);
  const [loading, setLoading] = useState(false);

  const { searchResult, searchHomepage } = useHomepageSearchQuery(() => {
    setLoading(false);
  });
  const { suggestions } = useSuggestionListQuery();
  const results = isShowSuggestion ? suggestions : searchResult;

  function handleFocusInput() {
    setPopupVisible(true);
    inputRef.current?.focus();
  }

  const triggerSearch = useMemo(
    () =>
      debounce((value: string) => {
        searchHomepage(value);
      }, SEARCH_DEBOUNCE),
    [searchHomepage],
  );

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;

    if (value) {
      triggerSearch(value.toLowerCase());
    }

    setLoading(true);
    setInputValue(value);
    setIsShowSuggestion(!value);
    setSelected(null);
    onChange?.(e);
  };

  const selectItem = useCallback(
    (result: HomepageSearchResult) => {
      setSelected(result);
      setInputValue(result.name);
      setPopupVisible(false);
      onSelectItem?.(result);
    },
    [onSelectItem],
  );

  const renderListItem = useCallback(
    (result: HomepageSearchResult) => {
      const { type, name } = result;
      const subheading = !type
        ? null
        : type.charAt(0).toUpperCase() + type.slice(1).toLowerCase();

      return (
        <ListItem
          icon={
            ICON_MAP[
              (type as SitewideResultTypeEnum) ?? SitewideResultTypeEnum.PLACE
            ] ?? faMapPin
          }
          iconColor={ColorEnum.GREEN}
          iconNoPadding={false}
          data-active={selected?.id === result.id}
          onClick={() => selectItem(result)}
        >
          <div data-component="content">
            <div>{name}</div>
            <div data-component="sub-heading">{subheading}</div>
          </div>
        </ListItem>
      );
    },
    [selected?.id, selectItem],
  );

  const searchInput = (
    <input
      ref={inputRef}
      id="search-autocomplete"
      type="search"
      placeholder={placeholder}
      autoComplete="off"
      spellCheck="false"
      onFocus={handleFocusInput}
      onChange={handleInputChange}
      value={inputValue}
    />
  );

  return (
    <section ref={wrapperRef} className={clsx(styles[ref], className)}>
      <label htmlFor="search-autocomplete">{label}</label>
      {searchInput}

      <Popup
        anchorEl={wrapperRef}
        title="Where"
        open={popupVisible}
        close={() => setPopupVisible(false)}
      >
        <div className={styles.popupInput}>{searchInput}</div>

        {isShowSuggestion || results.length ? (
          <section
            className={clsx(
              styles.results,
              isShowSuggestion && "showing-suggestions",
            )}
            aria-live="polite"
          >
            <List compact role="listbox">
              {results.map((result) => renderListItem(result))}
            </List>
          </section>
        ) : loading ? (
          <Loading inline />
        ) : (
          <section className={styles["no-results"]}>{noResultsText}</section>
        )}
      </Popup>
    </section>
  );
}

export default React.forwardRef<
  SearchAutocompleteHandle,
  SearchAutocompleteProps
>(SearchAutocomplete);
export type { SearchAutocompleteProps };
